import { supabase } from '../supabaseClient';
import type {
  AdminEvaluationPlanCategory,
  EvaluationLevelStepCount,
  EvaluationPlanAttachmentSlot,
  EvaluationPlanCategoryBundle,
  EvaluationPlanDraft,
  EvaluationPlanWorkspaceDraft,
  EvaluationStandardDraft,
} from '../types/evaluationPlan';
import { clampLevelStepCount, parseEvaluationPlanAttachment } from '../types/evaluationPlan';
import {
  clearAdminEvaluationPlanBundleCache,
  hydrateAdminEvaluationPlanBundleCache,
  loadAdminEvaluationPlanBundleCache,
  loadAdminEvaluationPlanCategoriesCache,
  patchAdminEvaluationPlanCategoriesCache,
  patchAdminEvaluationPlanBundleCache,
  renameAdminEvaluationPlanBundleCache,
  saveAdminEvaluationPlanBundleCache,
  saveAdminEvaluationPlanCategoriesCache,
  type CachedEvaluationPlanCategory,
} from '../utils/adminEvaluationPlanCache';
import {
  isAdminEvaluationPlanBundleSessionDbSynced,
  isAdminEvaluationPlanCategoriesSessionDbSynced,
  markAdminEvaluationPlanBundleSessionDbSynced,
  markAdminEvaluationPlanCategoriesSessionDbSynced,
} from '../utils/adminEvaluationPlanSessionSync';
import {
  listMissingAdminEvaluationPlanWorkspaceCategories,
  mergeAdminEvaluationPlanWorkspaceSnapshotFromLocal,
  type AdminEvaluationPlanWorkspaceSnapshot,
  type EvaluationPlanWorkspaceDraftSources,
} from '../utils/adminEvaluationPlanWorkspaceSnapshot';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import {
  buildEvaluationPlanBundleVersion,
  buildEvaluationPlanBundleVersionFromHeads,
  evaluationPlanScopeVersionFromHead,
  isSameEvaluationPlanBundleVersion,
} from '../utils/evaluationPlanCacheVersion';
import { buildLearningContentScopeVersion } from '../utils/learningContentCacheVersion';

const DELETE_IDS_CHUNK = 200;

const CATEGORY_SELECT = [
  'id',
  'workspace_id',
  'category_name',
  'level_step_count',
  'sort_order',
  'created_at',
  'updated_at',
].join(', ');
const STANDARD_SELECT = [
  'id',
  'workspace_id',
  'category_name',
  'track_text',
  'area_text',
  'achievement_code',
  'achievement_text',
  'level_a',
  'level_b',
  'level_c',
  'level_d',
  'level_e',
  'sort_order',
  'created_at',
  'updated_at',
].join(', ');
const PLAN_SELECT = [
  'id',
  'workspace_id',
  'category_name',
  'evaluation_element_text',
  'evaluation_method_text',
  'unit_text',
  'lesson_evaluation_text',
  'level_a',
  'level_b',
  'level_c',
  'level_d',
  'level_e',
  'level_step_count',
  'criterion_rubric_attachment',
  'evaluation_sheet_attachment',
  'sort_order',
  'created_at',
  'updated_at',
].join(', ');

function normalizePlanDraft(row: EvaluationPlanDraft, standardIds: string[] = []): EvaluationPlanDraft {
  const legacyStandardId =
    typeof (row as { standard_id?: unknown }).standard_id === 'string'
      ? ((row as { standard_id?: string }).standard_id as string)
      : null;
  const mergedStandardIds =
    standardIds.length > 0
      ? standardIds
      : Array.isArray(row.standard_ids)
        ? row.standard_ids
        : legacyStandardId
          ? [legacyStandardId]
          : [];
  return {
    ...row,
    category_name: row.category_name ?? '',
    standard_ids: [...mergedStandardIds],
    level_step_count: clampLevelStepCount(row.level_step_count),
    criterion_rubric_attachment: (() => {
      const parsed = parseEvaluationPlanAttachment(row.criterion_rubric_attachment);
      return parsed ? { ...parsed, slot: 'criterion' as const } : null;
    })(),
    evaluation_sheet_attachment: (() => {
      const parsed = parseEvaluationPlanAttachment(row.evaluation_sheet_attachment);
      return parsed ? { ...parsed, slot: 'sheet' as const } : null;
    })(),
  };
}

async function loadPlanStandardLinks(planIds: string[]): Promise<Map<string, string[]>> {
  const map = new Map<string, string[]>();
  if (!planIds.length) return map;
  for (let i = 0; i < planIds.length; i += DELETE_IDS_CHUNK) {
    const chunk = planIds.slice(i, i + DELETE_IDS_CHUNK);
    const { data, error } = await supabase
      .from('admin_evaluation_plan_standard_links')
      .select('plan_id, standard_id')
      .in('plan_id', chunk);
    if (error) throw new Error(error.message);
    for (const row of data ?? []) {
      const planId = String(row.plan_id);
      const list = map.get(planId) ?? [];
      list.push(String(row.standard_id));
      map.set(planId, list);
    }
  }
  return map;
}

async function syncPlanStandardLinks(plans: EvaluationPlanDraft[]): Promise<void> {
  for (const plan of plans) {
    const { error: deleteError } = await supabase
      .from('admin_evaluation_plan_standard_links')
      .delete()
      .eq('plan_id', plan.id);
    if (deleteError) throw new Error(deleteError.message);
    const standardIds = Array.from(new Set((plan.standard_ids ?? []).filter(Boolean)));
    if (!standardIds.length) continue;
    const { error: insertError } = await supabase.from('admin_evaluation_plan_standard_links').insert(
      standardIds.map((standardId) => ({
        plan_id: plan.id,
        standard_id: standardId,
      }))
    );
    if (insertError) throw new Error(insertError.message);
  }
}

async function deleteIdsInChunks(
  table: 'admin_evaluation_standards' | 'admin_evaluation_plans',
  ids: string[]
): Promise<void> {
  for (let i = 0; i < ids.length; i += DELETE_IDS_CHUNK) {
    const chunk = ids.slice(i, i + DELETE_IDS_CHUNK);
    const { error } = await supabase.from(table).delete().in('id', chunk);
    if (error) throw new Error(error.message);
  }
}

function normalizeAdminCategoryRow(row: AdminEvaluationPlanCategory): AdminEvaluationPlanCategory {
  return {
    ...row,
    level_step_count: clampLevelStepCount(row.level_step_count),
    level_label_a: '',
    level_label_b: '',
    level_label_c: '',
    level_label_d: '',
    level_label_e: '',
  };
}

function categoryRowToCache(row: AdminEvaluationPlanCategory): CachedEvaluationPlanCategory {
  return {
    id: row.id,
    category_name: row.category_name.trim(),
    sort_order: row.sort_order,
    level_step_count: row.level_step_count,
    updated_at: row.updated_at ?? '',
  };
}

function cachedCategoriesToRows(categories: CachedEvaluationPlanCategory[]): AdminEvaluationPlanCategory[] {
  return categories.map((row) =>
    normalizeAdminCategoryRow({
      id: row.id,
      workspace_id: null,
      category_name: row.category_name,
      level_step_count: clampLevelStepCount(row.level_step_count),
      level_label_a: '',
      level_label_b: '',
      level_label_c: '',
      level_label_d: '',
      level_label_e: '',
      sort_order: row.sort_order,
      created_at: '',
      updated_at: row.updated_at ?? '',
    })
  );
}

async function fetchAdminScopedTableHead(
  table: 'admin_evaluation_plan_categories' | 'admin_evaluation_standards' | 'admin_evaluation_plans',
  categoryName?: string
): Promise<string> {
  let countQuery = supabase.from(table).select('id', { count: 'exact', head: true }).is('workspace_id', null);
  if (categoryName) countQuery = countQuery.eq('category_name', categoryName);
  const { count, error: countError } = await countQuery;
  if (countError) throw new Error(countError.message);
  if (!count) return evaluationPlanScopeVersionFromHead(0, '');

  let maxQuery = supabase
    .from(table)
    .select('updated_at')
    .is('workspace_id', null)
    .order('updated_at', { ascending: false })
    .limit(1);
  if (categoryName) maxQuery = maxQuery.eq('category_name', categoryName);
  const { data, error } = await maxQuery;
  if (error) throw new Error(error.message);
  const maxUpdatedAt = (data?.[0] as { updated_at?: string } | undefined)?.updated_at ?? '';
  return evaluationPlanScopeVersionFromHead(count, maxUpdatedAt);
}

async function fetchAdminEvaluationPlanBundleHead(categoryName: string): Promise<string> {
  const name = categoryName.trim();
  const [standardsVersion, plansVersion] = await Promise.all([
    fetchAdminScopedTableHead('admin_evaluation_standards', name),
    fetchAdminScopedTableHead('admin_evaluation_plans', name),
  ]);
  return buildEvaluationPlanBundleVersionFromHeads({ standardsVersion, plansVersion });
}

/**
 * 플랫폼 전역 관리자 평가계획 카테고리.
 * LS 캐시가 있으면 head(`count:max(updated_at)`) 비교 후 동일하면 풀 fetch를 생략한다.
 */
export async function getAdminEvaluationPlanCategories(
  options?: { forceRefresh?: boolean }
): Promise<AdminEvaluationPlanCategory[]> {
  const cached = loadAdminEvaluationPlanCategoriesCache();
  if (!options?.forceRefresh && cached.found && cached.contentVersion) {
    try {
      const headVersion = await fetchAdminScopedTableHead('admin_evaluation_plan_categories');
      if (cached.contentVersion === headVersion) {
        markAdminEvaluationPlanCategoriesSessionDbSynced();
        if (process.env.NODE_ENV === 'development') {
          recordDevLocalStorageProgramCacheUse('평가계획관리·카테고리·LS');
        }
        return cachedCategoriesToRows(cached.categories);
      }
    } catch {
      /* head 실패 시 풀 조회로 진행 */
    }
  } else if (
    !options?.forceRefresh &&
    cached.found &&
    isAdminEvaluationPlanCategoriesSessionDbSynced()
  ) {
    if (process.env.NODE_ENV === 'development') {
      recordDevLocalStorageProgramCacheUse('평가계획관리·카테고리·LS');
    }
    return cachedCategoriesToRows(cached.categories);
  }
  const { data, error } = await supabase
    .from('admin_evaluation_plan_categories')
    .select(CATEGORY_SELECT)
    .is('workspace_id', null)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  const rows = ((data ?? []) as unknown as AdminEvaluationPlanCategory[]).map((row) => normalizeAdminCategoryRow(row));
  const cacheRows = rows.map((row) => categoryRowToCache(row));
  saveAdminEvaluationPlanCategoriesCache(cacheRows, {
    contentVersion: buildLearningContentScopeVersion(cacheRows),
  });
  markAdminEvaluationPlanCategoriesSessionDbSynced();
  return rows;
}

export async function createAdminEvaluationPlanCategory(
  categoryName: string,
  levelStepCount: EvaluationLevelStepCount = 5
): Promise<void> {
  const name = categoryName.trim();
  if (!name) throw new Error('카테고리 이름을 입력해 주세요.');
  const steps = clampLevelStepCount(levelStepCount);
  const now = new Date().toISOString();
  const { data: maxRow } = await supabase
    .from('admin_evaluation_plan_categories')
    .select('sort_order')
    .is('workspace_id', null)
    .order('sort_order', { ascending: false })
    .limit(1)
    .maybeSingle();
  const nextOrder = typeof maxRow?.sort_order === 'number' ? maxRow.sort_order + 1 : 0;
  const { error } = await supabase.from('admin_evaluation_plan_categories').insert({
    workspace_id: null,
    category_name: name,
    level_step_count: steps,
    sort_order: nextOrder,
    updated_at: now,
  });
  if (error) throw new Error(error.message);
  patchAdminEvaluationPlanCategoriesCache((list) => {
    if (list.some((row) => row.category_name === name)) return list;
    const maxOrder = list.reduce((max, row) => Math.max(max, row.sort_order ?? 0), -1);
    return [
      ...list,
      {
        id: `local-${name}`,
        category_name: name,
        sort_order: maxOrder + 1,
        level_step_count: steps,
      },
    ];
  });
  markAdminEvaluationPlanCategoriesSessionDbSynced();
}

export async function updateAdminEvaluationPlanCategoryLevelStep(
  categoryName: string,
  levelStepCount: EvaluationLevelStepCount
): Promise<void> {
  const name = categoryName.trim();
  if (!name) return;
  const steps = clampLevelStepCount(levelStepCount);
  const now = new Date().toISOString();
  const { error } = await supabase
    .from('admin_evaluation_plan_categories')
    .update({ level_step_count: steps, updated_at: now })
    .is('workspace_id', null)
    .eq('category_name', name);
  if (error) throw new Error(error.message);
  patchAdminEvaluationPlanCategoriesCache((list) =>
    list.map((row) => (row.category_name === name ? { ...row, level_step_count: steps } : row))
  );
  markAdminEvaluationPlanCategoriesSessionDbSynced();
}

export async function renameAdminEvaluationPlanCategory(fromName: string, toName: string): Promise<void> {
  const from = fromName.trim();
  const to = toName.trim();
  if (!from || !to || from === to) return;
  const now = new Date().toISOString();
  const { data: duplicate } = await supabase
    .from('admin_evaluation_plan_categories')
    .select('id')
    .is('workspace_id', null)
    .eq('category_name', to)
    .maybeSingle();
  if (duplicate?.id) throw new Error('이미 같은 이름의 카테고리가 있습니다.');
  const { error: catError } = await supabase
    .from('admin_evaluation_plan_categories')
    .update({ category_name: to, updated_at: now })
    .is('workspace_id', null)
    .eq('category_name', from);
  if (catError) throw new Error(catError.message);
  const { error: stdError } = await supabase
    .from('admin_evaluation_standards')
    .update({ category_name: to, updated_at: now })
    .is('workspace_id', null)
    .eq('category_name', from);
  if (stdError) throw new Error(stdError.message);
  const { error: planError } = await supabase
    .from('admin_evaluation_plans')
    .update({ category_name: to, updated_at: now })
    .is('workspace_id', null)
    .eq('category_name', from);
  if (planError) throw new Error(planError.message);
  patchAdminEvaluationPlanCategoriesCache((list) =>
    list.map((row) => (row.category_name === from ? { ...row, category_name: to } : row))
  );
  renameAdminEvaluationPlanBundleCache(from, to);
  markAdminEvaluationPlanCategoriesSessionDbSynced();
  markAdminEvaluationPlanBundleSessionDbSynced(to);
}

export async function deleteAdminEvaluationPlanCategory(categoryName: string): Promise<void> {
  const name = categoryName.trim();
  if (!name) return;
  const { data: plans, error: planFetchError } = await supabase
    .from('admin_evaluation_plans')
    .select('id')
    .is('workspace_id', null)
    .eq('category_name', name);
  if (planFetchError) throw new Error(planFetchError.message);
  const planIds = (plans ?? []).map((row) => row.id as string);
  if (planIds.length) {
    await deleteIdsInChunks('admin_evaluation_plans', planIds);
  }
  const { data: standards, error: stdFetchError } = await supabase
    .from('admin_evaluation_standards')
    .select('id')
    .is('workspace_id', null)
    .eq('category_name', name);
  if (stdFetchError) throw new Error(stdFetchError.message);
  const standardIds = (standards ?? []).map((row) => row.id as string);
  if (standardIds.length) {
    await deleteIdsInChunks('admin_evaluation_standards', standardIds);
  }
  const { error } = await supabase
    .from('admin_evaluation_plan_categories')
    .delete()
    .is('workspace_id', null)
    .eq('category_name', name);
  if (error) throw new Error(error.message);
  patchAdminEvaluationPlanCategoriesCache((list) => list.filter((row) => row.category_name !== name));
  clearAdminEvaluationPlanBundleCache(name);
  markAdminEvaluationPlanCategoriesSessionDbSynced();
}

export async function reorderAdminEvaluationPlanCategories(orderedCategoryNames: string[]): Promise<void> {
  const names = orderedCategoryNames.map((n) => n.trim()).filter(Boolean);
  if (!names.length) return;
  const { data, error } = await supabase
    .from('admin_evaluation_plan_categories')
    .select('id, category_name, sort_order')
    .is('workspace_id', null);
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Array<{ id: string; category_name: string; sort_order: number }>;
  const byName = new Map(rows.map((row) => [row.category_name.trim(), row]));
  const allNamesInCurrentOrder = [...rows]
    .sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id))
    .map((row) => row.category_name.trim());
  const used = new Set(names);
  const mergedNames = [...names, ...allNamesInCurrentOrder.filter((n) => !used.has(n))];
  const updates: Array<{ id: string; orderIndex: number; currentOrderIndex: number }> = [];
  mergedNames.forEach((n, idx) => {
    const row = byName.get(n);
    if (!row || row.sort_order === idx) return;
    updates.push({ id: row.id, orderIndex: idx, currentOrderIndex: row.sort_order });
  });
  if (!updates.length) return;
  const now = new Date().toISOString();
  for (const update of updates) {
    const { error: updateError } = await supabase
      .from('admin_evaluation_plan_categories')
      .update({ sort_order: update.currentOrderIndex + 1000, updated_at: now })
      .eq('id', update.id);
    if (updateError) throw new Error(updateError.message);
  }
  for (const update of updates) {
    const { error: updateError } = await supabase
      .from('admin_evaluation_plan_categories')
      .update({ sort_order: update.orderIndex, updated_at: now })
      .eq('id', update.id);
    if (updateError) throw new Error(updateError.message);
  }
  patchAdminEvaluationPlanCategoriesCache((list) => {
    const byNameMap = new Map(list.map((row) => [row.category_name, row]));
    const next: CachedEvaluationPlanCategory[] = [];
    mergedNames.forEach((n, idx) => {
      const row = byNameMap.get(n);
      if (row) next.push({ ...row, sort_order: idx });
    });
    return next;
  });
  markAdminEvaluationPlanCategoriesSessionDbSynced();
}

/**
 * 카테고리별 성취기준·평가계획 번들.
 * LS 캐시가 있으면 head 비교 후 동일하면 풀 fetch를 생략한다.
 */
export async function getAdminEvaluationPlanCategoryBundle(
  categoryName: string,
  options?: { forceRefresh?: boolean }
): Promise<EvaluationPlanCategoryBundle> {
  const name = categoryName.trim();
  if (!name) return { categoryName: '', standards: [], plans: [] };
  /** IndexedDB → 메모리 (없거나 IDB 불가면 그냥 진행 → 네트워크) */
  await hydrateAdminEvaluationPlanBundleCache(name);
  const cached = loadAdminEvaluationPlanBundleCache(name);
  if (!options?.forceRefresh && cached.found && cached.bundle && cached.contentVersion) {
    try {
      /** 버전 확인만(행 수 + max(updated_at) head 쿼리) — 같으면 행 다운로드 없음 */
      const headVersion = await fetchAdminEvaluationPlanBundleHead(name);
      if (isSameEvaluationPlanBundleVersion(cached.contentVersion, headVersion)) {
        markAdminEvaluationPlanBundleSessionDbSynced(name);
        if (process.env.NODE_ENV === 'development') {
          recordDevLocalStorageProgramCacheUse('평가계획관리·번들·LS');
        }
        return cached.bundle;
      }
    } catch {
      /* head 실패 시 풀 조회로 진행 */
    }
  } else if (
    !options?.forceRefresh &&
    cached.found &&
    cached.bundle &&
    isAdminEvaluationPlanBundleSessionDbSynced(name)
  ) {
    if (process.env.NODE_ENV === 'development') {
      recordDevLocalStorageProgramCacheUse('평가계획관리·번들·LS');
    }
    return cached.bundle;
  }
  const { data: standardsData, error: standardsError } = await supabase
    .from('admin_evaluation_standards')
    .select(STANDARD_SELECT)
    .is('workspace_id', null)
    .eq('category_name', name)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (standardsError) throw new Error(standardsError.message);
  const standards = ((standardsData ?? []) as unknown) as EvaluationStandardDraft[];
  const { data: plansData, error: plansError } = await supabase
    .from('admin_evaluation_plans')
    .select(PLAN_SELECT)
    .is('workspace_id', null)
    .eq('category_name', name)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (plansError) throw new Error(plansError.message);
  const rawPlans = (plansData ?? []) as unknown as EvaluationPlanDraft[];
  const planIds = rawPlans.map((row) => row.id);
  const linksByPlanId = await loadPlanStandardLinks(planIds);
  const plans = rawPlans.map((row) => normalizePlanDraft(row, linksByPlanId.get(row.id) ?? []));
  const bundle: EvaluationPlanCategoryBundle = { categoryName: name, standards, plans };
  saveAdminEvaluationPlanBundleCache(name, bundle, {
    contentVersion: buildEvaluationPlanBundleVersion(bundle),
  });
  markAdminEvaluationPlanBundleSessionDbSynced(name);
  return bundle;
}

export async function saveAdminEvaluationPlanCategoryBundle(
  draft: EvaluationPlanWorkspaceDraft
): Promise<EvaluationPlanCategoryBundle> {
  const categoryName = draft.categoryName.trim();
  if (!categoryName) throw new Error('카테고리를 선택해 주세요.');
  const now = new Date().toISOString();

  if (draft.deletedPlanIds.length) {
    await deleteIdsInChunks('admin_evaluation_plans', draft.deletedPlanIds);
  }
  if (draft.deletedStandardIds.length) {
    await deleteIdsInChunks('admin_evaluation_standards', draft.deletedStandardIds);
  }

  const standardUpserts = draft.standards.map((row, idx) => ({
    id: row.id,
    workspace_id: null,
    category_name: categoryName,
    track_text: row.track_text ?? '',
    area_text: row.area_text ?? '',
    achievement_code: row.achievement_code ?? '',
    achievement_text: row.achievement_text ?? '',
    level_a: row.level_a ?? '',
    level_b: row.level_b ?? '',
    level_c: row.level_c ?? '',
    level_d: row.level_d ?? '',
    level_e: row.level_e ?? '',
    sort_order: row.sort_order ?? idx,
    updated_at: now,
  }));
  if (standardUpserts.length) {
    const { error } = await supabase.from('admin_evaluation_standards').upsert(standardUpserts, { onConflict: 'id' });
    if (error) throw new Error(error.message);
  }

  const planUpserts = draft.plans.map((row, idx) => ({
    id: row.id,
    workspace_id: null,
    category_name: categoryName,
    evaluation_element_text: row.evaluation_element_text ?? '',
    evaluation_method_text: row.evaluation_method_text ?? '',
    unit_text: row.unit_text ?? '',
    lesson_evaluation_text: row.lesson_evaluation_text ?? '',
    level_a: row.level_a ?? '',
    level_b: row.level_b ?? '',
    level_c: row.level_c ?? '',
    level_d: row.level_d ?? '',
    level_e: row.level_e ?? '',
    level_step_count: clampLevelStepCount(row.level_step_count),
    criterion_rubric_attachment: row.criterion_rubric_attachment ?? null,
    evaluation_sheet_attachment: row.evaluation_sheet_attachment ?? null,
    sort_order: row.sort_order ?? idx,
    updated_at: now,
  }));
  if (planUpserts.length) {
    const { error } = await supabase.from('admin_evaluation_plans').upsert(planUpserts, { onConflict: 'id' });
    if (error) throw new Error(error.message);
  }

  await syncPlanStandardLinks(draft.plans);

  const bundle: EvaluationPlanCategoryBundle = {
    categoryName,
    standards: draft.standards,
    plans: draft.plans.map((row) => normalizePlanDraft(row, row.standard_ids ?? [])),
  };
  saveAdminEvaluationPlanBundleCache(categoryName, bundle, {
    contentVersion: buildEvaluationPlanBundleVersionFromHeads({
      standardsVersion: evaluationPlanScopeVersionFromHead(draft.standards.length, now),
      plansVersion: evaluationPlanScopeVersionFromHead(draft.plans.length, now),
    }),
  });
  markAdminEvaluationPlanBundleSessionDbSynced(categoryName);
  return bundle;
}

export type { AdminEvaluationPlanWorkspaceSnapshot };

function populateBundleCachesFromWorkspaceSnapshot(
  categoryNames: string[],
  snapshot: AdminEvaluationPlanWorkspaceSnapshot
): void {
  const standardsByCategory = new Map<string, EvaluationStandardDraft[]>();
  snapshot.standards.forEach((row) => {
    const cat = (row.category_name ?? '').trim();
    if (!cat) return;
    const list = standardsByCategory.get(cat) ?? [];
    list.push(row);
    standardsByCategory.set(cat, list);
  });
  const plansByCategory = new Map<string, EvaluationPlanDraft[]>();
  snapshot.plans.forEach((row) => {
    const cat = (row.category_name ?? '').trim();
    if (!cat) return;
    const list = plansByCategory.get(cat) ?? [];
    list.push(row);
    plansByCategory.set(cat, list);
  });
  for (const rawName of categoryNames) {
    const name = rawName.trim();
    if (!name) continue;
    const nextBundle: EvaluationPlanCategoryBundle = {
      categoryName: name,
      standards: standardsByCategory.get(name) ?? [],
      plans: plansByCategory.get(name) ?? [],
    };
    saveAdminEvaluationPlanBundleCache(name, nextBundle, {
      contentVersion: buildEvaluationPlanBundleVersion(nextBundle),
    });
    markAdminEvaluationPlanBundleSessionDbSynced(name);
  }
}

async function fetchAdminEvaluationPlanWorkspaceSnapshotFromDb(): Promise<AdminEvaluationPlanWorkspaceSnapshot> {
  const [standardsRes, plansRes] = await Promise.all([
    supabase
      .from('admin_evaluation_standards')
      .select(STANDARD_SELECT)
      .is('workspace_id', null)
      .order('category_name', { ascending: true })
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true }),
    supabase
      .from('admin_evaluation_plans')
      .select(PLAN_SELECT)
      .is('workspace_id', null)
      .order('category_name', { ascending: true })
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true }),
  ]);
  if (standardsRes.error) throw new Error(standardsRes.error.message);
  if (plansRes.error) throw new Error(plansRes.error.message);

  const standards = ((standardsRes.data ?? []) as unknown) as EvaluationStandardDraft[];
  const rawPlans = (plansRes.data ?? []) as unknown as EvaluationPlanDraft[];
  const planIds = rawPlans.map((row) => row.id);
  const linksByPlanId = await loadPlanStandardLinks(planIds);
  const plans = rawPlans.map((row) => normalizePlanDraft(row, linksByPlanId.get(row.id) ?? []));

  return { standards, plans };
}

/**
 * 로컬 캐시·세션 draft를 우선 사용한다.
 * 캐시가 전혀 없을 때만 전체 스냅샷을 한 번 받고,
 * 그 외에는 카테고리별 head 비교로 변경분만 풀 fetch한다.
 */
export async function resolveAdminEvaluationPlanWorkspaceSnapshot(
  sources: EvaluationPlanWorkspaceDraftSources
): Promise<AdminEvaluationPlanWorkspaceSnapshot> {
  const categoryNames = sources.categoryNames.map((name) => name.trim()).filter(Boolean);
  if (!categoryNames.length) {
    return { standards: [], plans: [] };
  }

  const missing = listMissingAdminEvaluationPlanWorkspaceCategories(sources);
  const hasLocalDraft = categoryNames.some((name) => Boolean(sources.draftsByCategory[name]));
  const allCategoriesMissing = missing.length === categoryNames.length && !hasLocalDraft;

  if (allCategoriesMissing) {
    const snapshot = await fetchAdminEvaluationPlanWorkspaceSnapshotFromDb();
    populateBundleCachesFromWorkspaceSnapshot(categoryNames, snapshot);
  } else {
    await Promise.all(categoryNames.map((name) => getAdminEvaluationPlanCategoryBundle(name)));
  }
  return mergeAdminEvaluationPlanWorkspaceSnapshotFromLocal(sources);
}

/** 카테고리 전체의 평가계획·성취기준 스냅샷(문서 목록 모달용) */
export async function getAllAdminEvaluationPlanWorkspaceSnapshot(): Promise<AdminEvaluationPlanWorkspaceSnapshot> {
  const categories = await getAdminEvaluationPlanCategories();
  const categoryNames = categories.map((row) => row.category_name.trim()).filter(Boolean);
  return resolveAdminEvaluationPlanWorkspaceSnapshot({ categoryNames, draftsByCategory: {} });
}

/** 평가계획 슬롯 첨부(기준안·평가지 문서) 제거 */
export async function clearAdminEvaluationPlanSlotAttachment(params: {
  planId: string;
  categoryName: string;
  slot: EvaluationPlanAttachmentSlot;
}): Promise<void> {
  const planId = params.planId.trim();
  const categoryName = params.categoryName.trim();
  if (!planId || !categoryName) throw new Error('삭제할 문서 정보가 없습니다.');

  const column = params.slot === 'criterion' ? 'criterion_rubric_attachment' : 'evaluation_sheet_attachment';
  const now = new Date().toISOString();
  const { error } = await supabase
    .from('admin_evaluation_plans')
    .update({ [column]: null, updated_at: now })
    .eq('id', planId)
    .is('workspace_id', null)
    .eq('category_name', categoryName);
  if (error) throw new Error(error.message);

  patchAdminEvaluationPlanBundleCache(categoryName, (prev) => ({
    ...prev,
    plans: prev.plans.map((plan) =>
      plan.id === planId
        ? params.slot === 'criterion'
          ? { ...plan, criterion_rubric_attachment: null }
          : { ...plan, evaluation_sheet_attachment: null }
        : plan
    ),
  }));
  markAdminEvaluationPlanBundleSessionDbSynced(categoryName);
}
